import {
  Alert,
  Button,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
} from "reactstrap";
import { SelectPicker, Tooltip, Whisper } from "rsuite";
import { useEffect, useMemo, useState } from "react";

import useGlobalLists from "../../../../api/api-hooks/cloud/useGlobalLists";
import AppColorPicker from "../../../../elements/AppColorPicker";
import ManageItems from "../../../ManageItems/ManageItems";
import CSVImporter from "../../../CRM/CSVImporter";
import StrUtils from "../../../../utils/StrUtils";

const listTypes = [
  {
    label: "Custom List",
    value: "CustomList",
    description: "Create a new custom list of options.",
  },
  {
    label: "Built-In List",
    value: "GlobalList",
    description: "Use a built-in list that's included with GrowthCloud.",
  },
];

interface Props {
  fieldValues: any;
  setFieldValues: React.Dispatch<React.SetStateAction<any>>;
  showModificationProtection?: boolean;
}

const FieldTypeSelect = ({
  fieldValues,
  setFieldValues,
  showModificationProtection,
}: Props) => {
  const [reordering, setReordering] = useState(false);
  const [importerOpen, setImporterOpen] = useState(false);
  const { globalLists } = useGlobalLists();
  const [listType, setListType] = useState<"CustomList" | "GlobalList" | null>(
    null
  );

  useEffect(() => {
    if (fieldValues?.source?.includes("Global.Lists.")) {
      setListType("GlobalList");
    } else if (fieldValues?.source && fieldValues.source.length > 0) {
      setListType("CustomList");
    }

    if (listType === "CustomList") {
      let changeOccured = false;
      const newSource = [...fieldValues.source];

      for (let i = 0; i < fieldValues.source.length; i++) {
        if (fieldValues.source[i].hasOwnProperty("id")) continue;

        changeOccured = true;
        fieldValues.source[i].id = StrUtils.uuidv4();
      }

      if (changeOccured) {
        setFieldValues((prev: any) => ({ ...prev, source: newSource }));
      }
    }
  }, [setFieldValues, fieldValues.source, listType]);

  const globalListsOptions = useMemo(() => {
    if (globalLists === undefined) {
      return;
    }

    const global = globalLists.map((g) => {
      return {
        label: g.Name,
        value: `Global.Lists.${g.APIName}`,
      };
    });
    return [...global];
  }, [globalLists]);

  const addBlankSourceValue = () => {
    if (typeof fieldValues.source != "object") {
      fieldValues.source = [];
    }

    let updatedSource = [
      ...fieldValues.source,
      { Name: "", Value: "", Color: "#e8edea", id: StrUtils.uuidv4() },
    ];

    // Cancel reorder when adding a new value
    if (reordering) setReordering(false);

    setFieldValues((prev: any) => ({ ...prev, source: updatedSource }));
  };

  const handleSelectImport = (data: any) => {
    let source = fieldValues.source || [];

    for (const item of data) {
      source.push({ Name: item.Name, Value: item.Value });
    }

    setFieldValues((prev: any) => ({ ...prev, source: source }));
  };

  const handleSourceChange = (e: any, index: number) => {
    let value = e.target.value;
    let name = e.target.name;

    let source = fieldValues.source;

    if (!source[index]) {
      source[index] = { Name: "", Value: "" };
    }

    if (name === "Name") {
      source[index].Name = value;
      source[index].Value = value;
    } else {
      source[index][name] = value;
    }

    // Reset the DefaultValue whenever this list changes
    setFieldValues((prev: any) => ({
      ...prev,
      source: source,
      default: false,
      defaultValue: "",
    }));
  };

  const handleSelectListType = (type: "CustomList" | "GlobalList" | null) => {
    setListType(type);
    setFieldValues((prev: any) => ({
      ...prev,
      source: [],
      default: false,
      defaultValue: "",
    }));
  };

  const handleSelectGlobalList = (listGlobalLists: string) => {
    setFieldValues((prev: any) => ({ ...prev, source: listGlobalLists }));
  };

  const removeSourceValue = ({ id }: any) => {
    const filteredSource = fieldValues.source.filter((s: any) => s.id !== id);

    setFieldValues((prev: any) => ({ ...prev, source: filteredSource }));
  };

  const handlePaste = (e: any, i: number) => {
    const pastedText = e.clipboardData.getData("text");

    if (!pastedText) return;

    if (pastedText.includes("\n") || pastedText.includes(",")) {
      let lines = pastedText.includes("\n")
        ? pastedText.split("\n")
        : pastedText.split(",");

      // Sanitize each line of text, removing quotes, single quotes, and commas
      lines = lines.flatMap((line: any) => {
        const newLine = line.trim().replace(/['",]/g, "");

        return newLine.length > 0 ? [newLine] : [];
      });

      for (let k = 0; k < lines.length; k++) {
        const line = lines[k];

        handleSourceChange(
          {
            target: {
              name: "Name",
              value: line,
            },
          },
          k + i
        );
      }
    }
  };

  useEffect(() => {
    if (listType === "CustomList" && fieldValues.source.length < 1) {
      addBlankSourceValue();
    }
  }, [listType]);

  return (
    <>
      <h3 className="mb-0 mt-3">List Type</h3>
      <p className="mb-2">
        <small>Select the type of list you want to add to this field.</small>
      </p>
      <div className="mb-4">
        <SelectPicker
          data={listTypes as any}
          searchable={false}
          preventOverflow
          block
          onChange={(type: "CustomList" | "GlobalList" | null) =>
            handleSelectListType(type)
          }
          value={listType}
          renderMenuItem={(label, item) => {
            return (
              <div style={{ maxWidth: 400 }}>
                <div
                  style={{ fontSize: 14 }}
                  className="text-dark font-weight-500"
                >
                  {label}
                </div>
                <div style={{ fontSize: 10 }}>{item.description}</div>
              </div>
            );
          }}
        />
      </div>

      {listType === "CustomList" ? (
        <div className="border-top">
          <h4 className="mb-0 mt-3">Selectable Field Options</h4>
          <div className="mb-2 small">
            Enter the variable name for the source of options to be offered with
            this field.
          </div>
          <Alert
            className="px-4 py-1 d-flex align-items-center font-weight-500"
            color="info"
          >
            <i className="mdi mdi-information-outline text-xl font-weight-500 mr-2"></i>
            Hint: You can paste in your options to have them added
            automatically.
          </Alert>
          <>
            {reordering ? (
              <>
                <h5 className="text-center">Reordering Options</h5>
                <ManageItems
                  itemKey={undefined}
                  items={fieldValues?.source || []}
                  onDelete={removeSourceValue}
                  onReorder={({ newOrder }) => {
                    setFieldValues((prev: any) => ({
                      ...prev,
                      source: newOrder.map((id) =>
                        fieldValues.source.find((s: any) => s.id === id)
                      ),
                    }));
                  }}
                />
              </>
            ) : (
              <>
                <div className="d-flex">
                  <h5>Label</h5>
                  <h5 style={{ marginLeft: "calc(50% - 78px)" }}>Value</h5>
                  <h5 style={{ marginLeft: `calc(50% - 85px)` }}>Color</h5>
                </div>
                {fieldValues?.source?.map((option: any, i: number) => (
                  <FormGroup className="mb-2" key={i}>
                    <InputGroup>
                      <Input
                        placeholder="Label"
                        required={true}
                        name={"Name"}
                        value={option.Name}
                        onChange={(e) => handleSourceChange(e, i)}
                        onPaste={(e) => {
                          e.preventDefault();
                          handlePaste(e, i);
                        }}
                        disabled={
                          fieldValues.protected && !showModificationProtection
                        }
                      />
                      <Input
                        placeholder="Value"
                        required={true}
                        name={"Value"}
                        value={option.Value}
                        onChange={(e) => handleSourceChange(e, i)}
                        disabled={
                          fieldValues.protected && !showModificationProtection
                        }
                      />
                      <InputGroupAddon addonType="append">
                        <AppColorPicker
                          value={option?.Color || "#e8edea"}
                          onChange={(newColor) =>
                            handleSourceChange(
                              {
                                target: { name: "Color", value: newColor },
                              },
                              i
                            )
                          }
                          inputStyles={{
                            height: "100%",
                            width: 50,
                            cursor: "pointer",
                          }}
                        />
                      </InputGroupAddon>
                      <InputGroupAddon addonType="append">
                        <Whisper
                          trigger="hover"
                          placement="autoHorizontal"
                          delayOpen={400}
                          speaker={<Tooltip>Remove option</Tooltip>}
                        >
                          <Button
                            disabled={fieldValues.source.length <= 1}
                            onClick={() => removeSourceValue({ id: option.id })}
                            color="danger"
                            type="button"
                            className="btn-icon-only"
                            style={{ height: 46, width: 46 }}
                          >
                            <i className="mdi mdi-close"></i>
                          </Button>
                        </Whisper>
                      </InputGroupAddon>
                    </InputGroup>
                  </FormGroup>
                ))}
              </>
            )}
            <div
              className="mb-3 mt-3 text-right"
              style={reordering ? { marginTop: 5 } : {}}
            >
              {!reordering && (
                <Whisper
                  trigger="hover"
                  placement="autoHorizontal"
                  delayOpen={400}
                  speaker={<Tooltip>Delete All Options</Tooltip>}
                >
                  <Button
                    size="sm"
                    onClick={() =>
                      setFieldValues((prev: any) => ({
                        ...prev,
                        source: [
                          {
                            Name: "",
                            Value: "",
                            Color: "#e8edea",
                            id: StrUtils.uuidv4(),
                          },
                        ],
                      }))
                    }
                    type="button"
                    outline
                    color="danger"
                    className="btn-icon-only mr-0"
                  >
                    <i className="mdi mdi-delete-sweep"></i>
                  </Button>
                </Whisper>
              )}
              <Whisper
                trigger={"hover"}
                placement="autoHorizontal"
                delayOpen={400}
                speaker={
                  <Tooltip>
                    {reordering ? "Save order" : "Reorder options"}
                  </Tooltip>
                }
              >
                <Button
                  size="sm"
                  color={reordering ? "primary" : "dark"}
                  onClick={() => setReordering((prev) => !prev)}
                  outline={!reordering}
                  className={`${!reordering && "btn-icon-only"}`}
                  type="button"
                >
                  {reordering ? (
                    "Save Order"
                  ) : (
                    <>
                      <i className="mdi mdi-swap-vertical-bold"></i>
                    </>
                  )}
                </Button>
              </Whisper>
              {!reordering && (
                <Whisper
                  trigger="hover"
                  placement="autoHorizontal"
                  delayOpen={400}
                  speaker={<Tooltip>Add new option</Tooltip>}
                >
                  <Button
                    size="sm"
                    onClick={addBlankSourceValue}
                    type="button"
                    color="primary"
                    className="btn-icon-only"
                  >
                    <i className="mdi mdi-plus"></i>
                  </Button>
                </Whisper>
              )}
            </div>
            <div className="py-3 border-top">
              <h4
                className="p-0 m-0 text-dark cursor-pointer"
                onClick={() => setImporterOpen((prev) => !prev)}
              >
                <i
                  className={`${
                    importerOpen
                      ? "mdi mdi-chevron-down"
                      : "mdi mdi-chevron-right"
                  } pr-2`}
                />
                Import options from CSV
              </h4>
            </div>
            {importerOpen && (
              <div className="mb-4">
                <CSVImporter
                  headers={["Name", "Value"]}
                  onSuccess={handleSelectImport}
                />
              </div>
            )}
          </>
        </div>
      ) : listType === "GlobalList" ? (
        <>
          <div className="py-3 border-top">
            <h4 className="mb-0 mt-3">Built-In List</h4>
            <p className="mb-3">
              <small>
                Select the built-in list you'd like to use with this field.
              </small>
            </p>
          </div>
          <div className="mb-4">
            <SelectPicker
              searchable={false}
              preventOverflow
              block
              onChange={(e) => {
                handleSelectGlobalList(e);
              }}
              value={fieldValues.source}
              data={globalListsOptions || []}
            />
          </div>
        </>
      ) : null}
    </>
  );
};

export default FieldTypeSelect;
